import { readOrgFile, listOrgCollection } from '../storage/FileSystemAdapter';
import { ALERT_CATALOG } from './alertCatalog';
import { MS_PER_DAY, DEFAULT_AUDIT_CYCLE_MONTHS, DEFAULT_MR_CYCLE_MONTHS, HEALTH_COMPONENT_WEIGHTS } from './constants';

// ─── HELPERS ──────────────────────────────────────────────────────────────────

/**
 * Current local date at midnight — the §7 "Temporal reference" bucket boundary.
 * All date-based alerts, score layers, and cutoffs use this (not the exact
 * wall-clock moment) so that two evaluations within the same local day produce
 * the same bucket result regardless of the hour at which they ran.
 */
function today() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Elapsed hours between `timestamp` and the current exact moment (not midnight).
 * This is the sole declared exception to the §7 midnight-boundary rule: four
 * regulatory alerts — gdprNotificationOverdue, nis2EarlyWarningOverdue,
 * nis2NotificationOverdue, nis2FinalReportOverdue — measure statutory hours
 * per GDPR Article 33 and NIS2 Article 23, and bucketing to local midnight
 * would push the alert up to one day early or late. Every other rule continues
 * to use today() above.
 */
function hoursAgo(timestamp) {
  return (new Date() - new Date(timestamp)) / 3600000;
}

/** (dateA - dateB) in days — positive when dateA is after dateB. */
function daysBetween(dateA, dateB) {
  return (new Date(dateA) - new Date(dateB)) / MS_PER_DAY;
}

/** Days elapsed since dateStr — positive when dateStr is in the past. */
function daysAgo(dateStr) {
  return daysBetween(today(), new Date(dateStr));
}

/** Add n calendar months to dateStr, clamping to last day of month if needed. */
function addMonths(dateStr, n) {
  const d = new Date(dateStr);
  const result = new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
  if (result.getDate() !== d.getDate()) result.setDate(0);
  return result;
}

function getGraceDays(frequency) {
  return { Weekly: 1, Monthly: 3, Quarterly: 9, Annual: 36 }[frequency] || 3;
}

function layerScore(checks) {
  if (checks.length === 0) return null;
  const passed = checks.filter(Boolean).length;
  return (passed / checks.length) * 100;
}

function clauseScoreAvg(existence, quality, validity) {
  const layers = [existence, quality, validity].filter(v => v !== null);
  if (layers.length === 0) return 0;
  return layers.reduce((s, v) => s + v, 0) / layers.length;
}

// ─── computeHealthScore ───────────────────────────────────────────────────────

export async function computeHealthScore(orgId, org) {
  const todayD = today();
  const currentYear = todayD.getFullYear();

  const [
    risks, soaRaw, objectives, metrics, audits, mrs,
    nonconformities, documents, assets, suppliers,
    awarenessRaw, competenceRaw,
  ] = await Promise.all([
    listOrgCollection(orgId, 'risks').catch(() => []),
    readOrgFile(orgId, 'controls/soa.json').catch(() => null),
    listOrgCollection(orgId, 'objectives').catch(() => []),
    listOrgCollection(orgId, 'metrics').catch(() => []),
    listOrgCollection(orgId, 'audits').catch(() => []),
    listOrgCollection(orgId, 'management-reviews').catch(() => []),
    listOrgCollection(orgId, 'nonconformities').catch(() => []),
    listOrgCollection(orgId, 'documents').catch(() => []),
    listOrgCollection(orgId, 'assets').catch(() => []),
    listOrgCollection(orgId, 'suppliers').catch(() => []),
    readOrgFile(orgId, `awareness/program-${currentYear}.json`).catch(() => null),
    readOrgFile(orgId, `competence/program-${currentYear}.json`).catch(() => null),
  ]);

  // ── riskTreatment (weight 15) ─────────────────────────────────────────────
  let riskTreatment = 0;
  const rtTotal = risks.length;
  if (rtTotal > 0) {
    let rtNum = 0;
    for (const r of risks) {
      if (r.treatmentActionStatus === 'Completed') rtNum += 1.0;
      else if (r.accepted === true) rtNum += 1.0;
      else if (r.treatmentActionStatus === 'In Progress') rtNum += 0.5;
    }
    riskTreatment = (rtNum / rtTotal) * HEALTH_COMPONENT_WEIGHTS.riskTreatment;
  }

  // ── Control implementation (weight 20) ───────────────────────────────────
  let controlImplementation = 0;
  const ciApplicable = soaRaw ? (soaRaw.controls || []).filter(c => c.applicable) : [];
  if (ciApplicable.length > 0) {
    // Implementation-based rule: "Implemented" controls contribute 1.0,
    // "Partially implemented" 0.5, anything else 0. The mean over applicable
    // controls is scaled by the component weight.
    let ciSum = 0;
    for (const c of ciApplicable) {
      if (c.implementationStatus === 'Implemented') ciSum += 1.0;
      else if (c.implementationStatus === 'Partially implemented') ciSum += 0.5;
    }
    controlImplementation = (ciSum / ciApplicable.length) * HEALTH_COMPONENT_WEIGHTS.controlImplementation;
  }

  // ── objectiveAchievement (weight 10) ─────────────────────────────────────
  let objectiveAchievement = 0;
  const oaTotal = objectives.length;
  if (oaTotal > 0) {
    let oaNum = 0;
    for (const o of objectives) {
      if (o.status === 'Achieved') {
        oaNum += 1.0;
      } else if (o.status === 'On Track') {
        const hasOnCadenceMetric = metrics.some(met => {
          if (met.objectiveId !== o.id) return false;
          const grace = getGraceDays(met.frequency);
          const readings = Array.isArray(met.readings) ? met.readings : [];
          let expectedNext;
          if (readings.length === 0) {
            const base = (met.updatedAt || '').slice(0, 10) || todayD.toISOString().slice(0, 10);
            if (met.frequency === 'Weekly') {
              expectedNext = new Date(new Date(base).getTime() + 7 * MS_PER_DAY);
            } else {
              const periods = { Monthly: 1, Quarterly: 3, Annual: 12 };
              expectedNext = addMonths(base, periods[met.frequency] || 1);
            }
          } else {
            const sorted = readings.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
            const lastDate = sorted[0].date;
            if (met.frequency === 'Weekly') {
              expectedNext = new Date(new Date(lastDate).getTime() + 7 * MS_PER_DAY);
            } else {
              const periods = { Monthly: 1, Quarterly: 3, Annual: 12 };
              expectedNext = addMonths(lastDate, periods[met.frequency] || 1);
            }
          }
          const deadline = new Date(expectedNext.getTime() + grace * MS_PER_DAY);
          return todayD <= deadline;
        });
        if (hasOnCadenceMetric) oaNum += 0.5;
      }
    }
    objectiveAchievement = (oaNum / oaTotal) * HEALTH_COMPONENT_WEIGHTS.objectiveAchievement;
  }

  // ── auditCadence (weight 5) ───────────────────────────────────────────────
  let auditCadence = 0;
  const acCompleted = audits
    .filter(a => a.type === 'Internal' && a.status === 'Completed' && (a.actualDate || a.plannedDate))
    .sort((a, b) => new Date(b.actualDate || b.plannedDate) - new Date(a.actualDate || a.plannedDate));
  if (acCompleted.length > 0) {
    const acLastDate = acCompleted[0].actualDate || acCompleted[0].plannedDate;
    const acCycleMonths = org.auditFrequencyMonths || DEFAULT_AUDIT_CYCLE_MONTHS;
    // Calendar-month frontiers per §7 "Frontier semantics".
    const acCycleEnd = addMonths(acLastDate, acCycleMonths);
    const acGrace3   = addMonths(acLastDate, acCycleMonths + 3);
    const acGrace6   = addMonths(acLastDate, acCycleMonths + 6);
    if (todayD <= acCycleEnd) auditCadence = 5;
    else if (todayD <= acGrace3) auditCadence = 3;
    else if (todayD <= acGrace6) auditCadence = 1;
  }

  // ── mrCadence (weight 5) ──────────────────────────────────────────────────
  let mrCadence = 0;
  const mrCompleted = mrs
    .filter(mr => mr.approvalDate && mr.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  if (mrCompleted.length > 0) {
    const mrLastDate = mrCompleted[0].date;
    const mrCycleMonths = org.mrFrequencyMonths || DEFAULT_MR_CYCLE_MONTHS;
    const mrCycleEnd = addMonths(mrLastDate, mrCycleMonths);
    const mrGrace3   = addMonths(mrLastDate, mrCycleMonths + 3);
    const mrGrace6   = addMonths(mrLastDate, mrCycleMonths + 6);
    if (todayD <= mrCycleEnd) mrCadence = 5;
    else if (todayD <= mrGrace3) mrCadence = 3;
    else if (todayD <= mrGrace6) mrCadence = 1;
  }

  // ── ncClosure (weight 10) ─────────────────────────────────────────────────
  // Empty register contributes 0: absence of NCs is not evidence of closure
  // discipline, just absence of work done.
  let ncClosure = 0;
  const ncTotal = nonconformities.length;
  if (ncTotal > 0) {
    const ncClosed = nonconformities.filter(nc => nc.status === 'Closed').length;
    const ncOpenOldCount = nonconformities.filter(nc => {
      if (nc.status === 'Closed') return false;
      const ref = nc.createdAt || nc.date;
      return ref && daysAgo(ref) > 90;
    }).length;
    // §7 clamps the result to [0, 10]. The upper bound is defensive — the
    // base formula cannot exceed HEALTH_COMPONENT_WEIGHTS.ncClosure today, but hardening
    // against future weight changes keeps the clamp range in code
    // mechanically consistent with the spec.
    ncClosure = Math.min(
      HEALTH_COMPONENT_WEIGHTS.ncClosure,
      Math.max(0, (ncClosed / ncTotal) * HEALTH_COMPONENT_WEIGHTS.ncClosure - ncOpenOldCount)
    );
  }

  // ── documentCurrency (weight 8) ───────────────────────────────────────────
  let documentCurrency = 0;
  const dcApproved = documents.filter(d => d.status === 'Approved');
  if (dcApproved.length > 0) {
    const dcCurrent = dcApproved.filter(
      d => d.nextReviewDate && new Date(d.nextReviewDate) >= todayD
    ).length;
    documentCurrency = (dcCurrent / dcApproved.length) * HEALTH_COMPONENT_WEIGHTS.documentCurrency;
  }

  // ── awarenessCoverage (weight 7) ──────────────────────────────────────────
  let awarenessCoverage = 0;
  if (awarenessRaw) {
    const awCompleted = (awarenessRaw.activities || []).filter(
      a => a.status === 'Completed' && a.coveragePercent != null
    );
    if (awCompleted.length > 0) {
      const awAvg = awCompleted.reduce((s, a) => s + a.coveragePercent, 0) / awCompleted.length;
      awarenessCoverage = (awAvg / 100) * HEALTH_COMPONENT_WEIGHTS.awarenessCoverage;
    }
  }

  // ── competenceCompletion (weight 7) ───────────────────────────────────────
  let competenceCompletion = 0;
  if (competenceRaw) {
    // §7 Health score "Competence completion" — denominator excludes training
    // actions with status === null so unplanned/unassigned actions cannot
    // inflate the divisor.
    const ccActions = (competenceRaw.roles || []).flatMap(r => r.trainingActions || [])
      .filter(a => a.status != null);
    if (ccActions.length > 0) {
      const ccDone = ccActions.filter(a => a.status === 'Completed').length;
      competenceCompletion = (ccDone / ccActions.length) * HEALTH_COMPONENT_WEIGHTS.competenceCompletion;
    }
  }

  // ── supplierReviewCadence (weight 8) ──────────────────────────────────────
  // Empty register contributes 0: absence of suppliers is not evidence of
  // review cadence, just absence of work done.
  let supplierReviewCadence = 0;
  if (suppliers.length > 0) {
    let srWeightedTotal = 0;
    let srWeightedReviewed = 0;
    for (const s of suppliers) {
      const w = s.criticality === 'Critical' ? 2 : s.criticality === 'High' ? 1.5 : 1;
      srWeightedTotal += w;
      if (s.nextReviewDate && new Date(s.nextReviewDate) >= todayD) {
        srWeightedReviewed += w;
      }
    }
    if (srWeightedTotal > 0) {
      supplierReviewCadence = (srWeightedReviewed / srWeightedTotal) * HEALTH_COMPONENT_WEIGHTS.supplierReviewCadence;
    }
  }

  // ── assetInventoryHealth (weight 5) ───────────────────────────────────────
  let assetInventoryHealth = 0;
  const aiTotal = assets.length;
  if (aiTotal > 0) {
    const aiQualified = assets.filter(a =>
      a.owner &&
      a.classification &&
      a.dimensions?.confidentiality != null &&
      a.dimensions?.integrity != null &&
      a.dimensions?.availability != null
    ).length;
    assetInventoryHealth = (aiQualified / aiTotal) * HEALTH_COMPONENT_WEIGHTS.assetInventoryHealth;
  }

  // ── Assemble ──────────────────────────────────────────────────────────────
  const components = {
    riskTreatment,
    controlImplementation,
    objectiveAchievement,
    auditCadence,
    mrCadence,
    ncClosure,
    documentCurrency,
    awarenessCoverage,
    competenceCompletion,
    supplierReviewCadence,
    assetInventoryHealth,
  };

  let score = Math.round(
    Math.min(100, Math.max(0, Object.values(components).reduce((s, v) => s + v, 0)))
  );

  // ── Hard gate ─────────────────────────────────────────────────────────────
  // Kept in sync with Compliance score hard gates (see §7). Six conditions —
  // identical set, identical in-cycle semantics for audit and MR.
  const effectiveCertStatus = org.certificationStatus || 'none';
  let hardGateFired = false;
  if (effectiveCertStatus !== 'none') {
    const [rtpRaw, scopeRaw] = await Promise.all([
      readOrgFile(orgId, 'rtp.json').catch(() => null),
      readOrgFile(orgId, 'scope.json').catch(() => null),
    ]);
    // Calendar-month cutoffs per §7 "Frontier semantics".
    const auditCutoffHg = addMonths(todayD, -(org.auditFrequencyMonths || DEFAULT_AUDIT_CYCLE_MONTHS));
    const mrCutoffHg    = addMonths(todayD, -(org.mrFrequencyMonths || DEFAULT_MR_CYCLE_MONTHS));
    const gateChecks = [
      documents.some(d => d.isInformationSecurityPolicy === true && d.status === 'Approved' && d.approvalDate),
      !!(soaRaw && soaRaw.approvalDate),
      !!(rtpRaw && rtpRaw.approvalDate),
      !!(scopeRaw && scopeRaw.approvalDate),
      audits.some(a =>
        a.type === 'Internal' && a.status === 'Completed' &&
        a.actualDate && new Date(a.actualDate) >= auditCutoffHg
      ),
      mrs.some(mr => mr.approvalDate && mr.date && new Date(mr.date) >= mrCutoffHg),
    ];
    if (gateChecks.some(c => !c)) {
      hardGateFired = true;
      score = Math.min(score, 60);
    }
  }

  return { score, components, hardGateFired };
}

// ─── computeComplianceScore ───────────────────────────────────────────────────

export async function computeComplianceScore(orgId, org) {
  const todayD = today();
  const currentYear = todayD.getFullYear();

  const CLAUSE_WEIGHTS = { cl4: 10, cl5: 10, cl6: 15, cl7: 12, cl8: 10, cl9: 12, cl10: 6 };
  const ANNEX_WEIGHTS  = { a5: 10, a6: 2, a7: 4, a8: 9 };

  const [
    contextRaw, scopeRaw, soaRaw, rtpRaw,
    risks, documents, objectives, metrics,
    assets, suppliers, incidents,
    audits, mrs, nonconformities,
    awarenessRaw, competenceRaw,
  ] = await Promise.all([
    readOrgFile(orgId, 'context.json').catch(() => null),
    readOrgFile(orgId, 'scope.json').catch(() => null),
    readOrgFile(orgId, 'controls/soa.json').catch(() => null),
    readOrgFile(orgId, 'rtp.json').catch(() => null),
    listOrgCollection(orgId, 'risks').catch(() => []),
    listOrgCollection(orgId, 'documents').catch(() => []),
    listOrgCollection(orgId, 'objectives').catch(() => []),
    listOrgCollection(orgId, 'metrics').catch(() => []),
    listOrgCollection(orgId, 'assets').catch(() => []),
    listOrgCollection(orgId, 'suppliers').catch(() => []),
    listOrgCollection(orgId, 'incidents').catch(() => []),
    listOrgCollection(orgId, 'audits').catch(() => []),
    listOrgCollection(orgId, 'management-reviews').catch(() => []),
    listOrgCollection(orgId, 'nonconformities').catch(() => []),
    readOrgFile(orgId, `awareness/program-${currentYear}.json`).catch(() => null),
    readOrgFile(orgId, `competence/program-${currentYear}.json`).catch(() => null),
  ]);

  const soaControls = soaRaw ? (soaRaw.controls || []) : [];
  const ctx   = contextRaw || {};
  const sc    = scopeRaw   || {};
  const roles = org.ismsRoles || {};

  const ispDoc = documents.find(d => d.isInformationSecurityPolicy === true);
  const ispApproved = !!(ispDoc && ispDoc.status === 'Approved' && ispDoc.approvalDate);

  // ── Clause 4 — Context ──────────────────────────────────────────────────
  const cl4Existence = [
    Array.isArray(ctx.internalIssues) && ctx.internalIssues.length >= 1,
    Array.isArray(ctx.externalIssues) && ctx.externalIssues.length >= 1,
    Array.isArray(ctx.interestedParties) && ctx.interestedParties.length >= 1,
    !!(sc.description && String(sc.description).trim() !== ''),
  ];
  const cl4Quality = [
    Array.isArray(ctx.interestedParties) && ctx.interestedParties.some(p =>
      Array.isArray(p.requirements) && p.requirements.length >= 1
    ),
    (Array.isArray(sc.interfaces) && sc.interfaces.length >= 1) ||
    (Array.isArray(sc.exclusions) && sc.exclusions.length >= 1),
    !!sc.approvalDate,
  ];
  const cl4Validity = [
    !!(ctx.reviewDate && daysAgo(ctx.reviewDate) <= 365),
    !!(sc.approvalDate && daysAgo(sc.approvalDate) <= 730),
  ];

  // ── Clause 5 — Leadership ───────────────────────────────────────────────
  const _tmsRaw = roles.topManagementSignatory;
  const _tmsHasValue = Array.isArray(_tmsRaw) ? _tmsRaw.length >= 1 : (typeof _tmsRaw === 'string' && _tmsRaw !== '');
  const cl5Existence = [
    !!ispDoc,
    _tmsHasValue,
    !!roles.ismsManager,
  ];
  const cl5Quality = [
    ispApproved,
    !!org.topManagementCommitmentEvidenceId,
  ];
  const cl5Validity = [
    !!(ispDoc && ispDoc.nextReviewDate &&
      new Date(ispDoc.nextReviewDate) >= todayD),
  ];

  // ── Clause 6 — Planning ─────────────────────────────────────────────────
  const cl6Existence = [
    risks.length >= 1,
    risks.length >= 1 && ['LOW', 'MEDIUM', 'HIGH'].includes(org.riskAcceptanceThreshold) &&
      !!(org.riskMatrix && org.riskMatrix.thresholds),
    !!soaRaw && soaControls.some(c => 'applicable' in c),
    !!rtpRaw,
    objectives.length >= 1,
  ];
  const cl6Quality = [
    risks.length > 0 && risks.every(r => r.owner && r.owner !== '' && r.riskLevel != null),
    !!(soaRaw && soaRaw.approvalDate && soaRaw.approvalEvidenceId),
    !!(rtpRaw && rtpRaw.approvalDate && rtpRaw.approvalEvidenceId),
    objectives.length > 0 && objectives.every(o =>
      o.measurementMethod && o.measurementMethod !== '' &&
      Array.isArray(o.linkedMetricIds) && o.linkedMetricIds.length >= 1
    ),
  ];
  const cl6Validity = [
    risks.some(r =>
      (r.identifiedDate && daysAgo(r.identifiedDate) <= 365) ||
      (r.nextReviewDate && daysAgo(r.nextReviewDate) <= 365)
    ),
    objectives.some(o => {
      if (!Array.isArray(o.linkedMetricIds) || o.linkedMetricIds.length === 0) return false;
      return o.linkedMetricIds.some(mid => {
        const met = metrics.find(m => m.id === mid);
        if (!met || !Array.isArray(met.readings)) return false;
        return met.readings.some(rd => rd.date && new Date(rd.date).getFullYear() === currentYear);
      });
    }),
  ];

  // ── Clause 7 — Support ──────────────────────────────────────────────────
  const competenceActions = competenceRaw
    ? (competenceRaw.roles || []).flatMap(r => r.trainingActions || [])
    : [];
  const cl7Existence = [
    !!(competenceRaw && Array.isArray(competenceRaw.roles) && competenceRaw.roles.length >= 1),
    !!(awarenessRaw && Array.isArray(awarenessRaw.activities) && awarenessRaw.activities.length >= 1),
    documents.length >= 3,
  ];
  const cl7Quality = [
    competenceActions.length > 0 && competenceActions.every(a => a.responsible && a.deadline),
    documents.length > 0 && documents.every(d =>
      d.owner && d.owner !== '' && d.classification && d.classification !== ''
    ),
  ];
  const cl7Validity = [
    documents.length > 0 && !documents.some(d =>
      d.status === 'Approved' && d.nextReviewDate && new Date(d.nextReviewDate) < todayD
    ),
    !!(awarenessRaw && (awarenessRaw.activities || []).some(a => {
      if (a.status !== 'Completed') return false;
      if (!a.completedDate) return true;
      return new Date(a.completedDate).getFullYear() === currentYear;
    })),
  ];

  // ── Clause 8 — Operation ────────────────────────────────────────────────
  const cl8Existence = [
    assets.length >= 1,
    incidents.length >= 1,
  ];
  const cl8Quality = [
    assets.length > 0 && assets.every(a =>
      a.owner && a.owner !== '' && a.classification && a.classification !== ''
    ),
    suppliers.length > 0 && suppliers.every(s => !!s.criticality),
    incidents.length > 0 && !incidents.some(i =>
      i.status === 'Closed' &&
      (i.severity === 'Critical' || i.severity === 'High') &&
      (!i.rootCause || i.rootCause === '') &&
      i.detectedAt && daysAgo(i.detectedAt) > 30
    ),
  ];
  const cl8Validity = [
    assets.some(a => a.updatedAt && daysAgo(a.updatedAt) <= 365),
    suppliers.length > 0 && !suppliers.some(s => s.nextReviewDate && new Date(s.nextReviewDate) < todayD),
  ];

  // ── Clause 9 — Performance evaluation ───────────────────────────────────
  // Calendar-month cutoffs per §7 "Frontier semantics".
  const auditCutoff = addMonths(todayD, -(org.auditFrequencyMonths || DEFAULT_AUDIT_CYCLE_MONTHS));
  const mrCutoff    = addMonths(todayD, -(org.mrFrequencyMonths || DEFAULT_MR_CYCLE_MONTHS));

  const internalAudits = audits.filter(a => a.type === 'Internal');
  const completedAudits = internalAudits.filter(a => a.status === 'Completed');
  const completedAuditInCycle = completedAudits.find(a =>
    a.actualDate && new Date(a.actualDate) >= auditCutoff
  );
  const completedAuditWithScope = completedAudits.find(a =>
    a.scope && a.scope !== '' && a.criteria && a.criteria !== ''
  );

  const completedMRs = mrs
    .filter(mr => mr.approvalDate && mr.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  const latestMR = completedMRs[0];
  const mrInCycle = completedMRs.find(mr => new Date(mr.date) >= mrCutoff);

  const REQUIRED_INPUTS = [
    'previousActionsStatus', 'externalInternalChanges', 'interestedPartiesChanges',
    'interestedPartiesFeedback', 'riskTreatmentStatus', 'improvementOpportunities',
    'climateChangeConsiderations',
  ];
  const REQUIRED_PERF = ['nonconformitiesTrend', 'monitoringResults', 'auditResults', 'objectivesFulfilment'];
  let mrInputsOk = false;
  if (latestMR) {
    const inp = latestMR.inputs || {};
    const pf  = inp.performanceFeedback || {};
    mrInputsOk =
      REQUIRED_INPUTS.every(k => inp[k] && String(inp[k]).trim() !== '') &&
      REQUIRED_PERF.every(k => pf[k] && String(pf[k]).trim() !== '') &&
      Array.isArray(latestMR.decisions) && latestMR.decisions.length >= 1;
  }

  const metricReadingsOk = metrics.length > 0 && !metrics.some(met => {
    const grace = getGraceDays(met.frequency);
    const readings = Array.isArray(met.readings) ? met.readings : [];
    let expectedNext;
    if (readings.length === 0) {
      const base = (met.updatedAt || '').slice(0, 10) || todayD.toISOString().slice(0, 10);
      if (met.frequency === 'Weekly') {
        expectedNext = new Date(new Date(base).getTime() + 7 * MS_PER_DAY);
      } else {
        const periods = { Monthly: 1, Quarterly: 3, Annual: 12 };
        expectedNext = addMonths(base, periods[met.frequency] || 1);
      }
    } else {
      const sorted = readings.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
      const lastDate = sorted[0].date;
      if (met.frequency === 'Weekly') {
        expectedNext = new Date(new Date(lastDate).getTime() + 7 * MS_PER_DAY);
      } else {
        const periods = { Monthly: 1, Quarterly: 3, Annual: 12 };
        expectedNext = addMonths(lastDate, periods[met.frequency] || 1);
      }
    }
    const deadline = new Date(expectedNext.getTime() + grace * MS_PER_DAY);
    return todayD > deadline;
  });

  const cl9Existence = [
    metrics.length >= 3,
    internalAudits.length >= 1,
    mrs.length >= 1,
  ];
  const cl9Quality = [
    metrics.length > 0 && metrics.every(m =>
      m.frequency && m.measurementMethod && m.measurementMethod !== ''
    ),
    !!completedAuditWithScope,
    mrInputsOk,
  ];
  const cl9Validity = [
    !!completedAuditInCycle,
    !!mrInCycle,
    metricReadingsOk,
  ];

  // ── Clause 10 — Improvement ─────────────────────────────────────────────
  const openNcs   = nonconformities.filter(nc => nc.status !== 'Closed');
  const closedNcs = nonconformities.filter(nc => nc.status === 'Closed');
  // Calendar-month 12m cutoff per §7 "Frontier semantics".
  const ncs12mCutoff = addMonths(todayD, -12);
  const ncsLast12m = nonconformities.filter(nc => {
    const ref = nc.createdAt || nc.date;
    return ref && new Date(ref) >= ncs12mCutoff;
  });
  const closedLast12m = ncsLast12m.filter(nc => nc.status === 'Closed');

  const cl10Existence = [
    nonconformities.length >= 1,
  ];
  const cl10Quality = [
    openNcs.length > 0 && !openNcs.some(nc => !Array.isArray(nc.rootCauses) || nc.rootCauses.length === 0),
    closedNcs.length > 0 && !closedNcs.some(nc => !nc.verifiedBy),
  ];
  const cl10Validity = [
    nonconformities.length > 0 && !nonconformities.some(nc =>
      nc.status !== 'Closed' &&
      Array.isArray(nc.correctiveActions) &&
      nc.correctiveActions.some(ca => ca.deadline && new Date(ca.deadline) < todayD)
    ),
    ncsLast12m.length >= 3 && (closedLast12m.length / ncsLast12m.length) >= 0.70,
  ];

  const clauseScores = {
    cl4:  clauseScoreAvg(layerScore(cl4Existence),  layerScore(cl4Quality),  layerScore(cl4Validity)),
    cl5:  clauseScoreAvg(layerScore(cl5Existence),  layerScore(cl5Quality),  layerScore(cl5Validity)),
    cl6:  clauseScoreAvg(layerScore(cl6Existence),  layerScore(cl6Quality),  layerScore(cl6Validity)),
    cl7:  clauseScoreAvg(layerScore(cl7Existence),  layerScore(cl7Quality),  layerScore(cl7Validity)),
    cl8:  clauseScoreAvg(layerScore(cl8Existence),  layerScore(cl8Quality),  layerScore(cl8Validity)),
    cl9:  clauseScoreAvg(layerScore(cl9Existence),  layerScore(cl9Quality),  layerScore(cl9Validity)),
    cl10: clauseScoreAvg(layerScore(cl10Existence), layerScore(cl10Quality), layerScore(cl10Validity)),
  };

  // ── Annex A scores ──────────────────────────────────────────────────────
  const annexAScores = {};
  for (const sec of ['5', '6', '7', '8']) {
    const applicable = soaControls.filter(c => {
      if (!c.applicable) return false;
      const rawId = (c.id || '').replace(/^iso27001:/, '');
      return rawId.startsWith(`${sec}.`);
    });
    const key = `a${sec}`;
    if (applicable.length === 0) {
      annexAScores[key] = 0;
    } else {
      const implemented = applicable.filter(c => c.implementationStatus === 'Implemented').length;
      const partial     = applicable.filter(c => c.implementationStatus === 'Partially implemented').length;
      annexAScores[key] = Math.round((implemented + partial * 0.5) / applicable.length * 100);
    }
  }

  // ── Total score (75% clauses + 25% Annex A) ─────────────────────────────
  let total = 0;
  for (const [k, w] of Object.entries(CLAUSE_WEIGHTS)) total += (clauseScores[k] / 100) * w;
  for (const [k, w] of Object.entries(ANNEX_WEIGHTS))  total += (annexAScores[k]  / 100) * w;
  let score = Math.round(Math.min(100, Math.max(0, total)));

  // ── Hard gates ──────────────────────────────────────────────────────────
  // Six conditions, identical to the Health score hard gate (see §7).
  const gateChecks = [
    ispApproved,
    !!(soaRaw   && soaRaw.approvalDate),
    !!(rtpRaw   && rtpRaw.approvalDate),
    !!(scopeRaw && scopeRaw.approvalDate),
    !!completedAuditInCycle,
    !!mrInCycle,
  ];
  const hardGateFired = gateChecks.some(c => !c);

  const effectiveCertStatus = org.certificationStatus || 'none';
  if (hardGateFired && effectiveCertStatus !== 'none') {
    score = Math.min(score, 60);
  }

  return { score, clauseScores, annexAScores, hardGateFired, trackedCoverage: 95 };
}

// ─── computeAlerts ────────────────────────────────────────────────────────────

export async function computeAlerts(orgId, org) {
  const todayD = today();
  const currentYear = todayD.getFullYear();
  const snoozes = org.alertSnoozes || {};
  const effectiveCertStatus = org.certificationStatus || 'none';

  function snoozed(key) {
    return snoozes[key] && new Date(snoozes[key]) > todayD;
  }

  function makeAlert(key, entityIds, count) {
    const entry = ALERT_CATALOG[key];
    if (!entry) throw new Error(`Unknown alert key: ${key}`);
    if (entry.gatedOnIsmsIntent && effectiveCertStatus === 'none') return null;
    if (snoozed(key)) return null;
    const ids = entityIds || [];
    const effectiveCount = count != null ? count : ids.length;
    if (effectiveCount === 0) return null;
    return {
      key,
      severity: entry.severity,
      titleKey: key,
      count: effectiveCount,
      entityIds: ids,
    };
  }

  const [
    risks, soaRaw, rtpRaw, objectives, assets, suppliers, customers, incidents,
    documents, evidence, audits, mrs, nonconformities,
    contextRaw, scopeRaw, peopleRaw, awarenessRaw, competenceRaw,
    metrics, legalRegister,
  ] = await Promise.all([
    listOrgCollection(orgId, 'risks').catch(() => []),
    readOrgFile(orgId, 'controls/soa.json').catch(() => null),
    readOrgFile(orgId, 'rtp.json').catch(() => null),
    listOrgCollection(orgId, 'objectives').catch(() => []),
    listOrgCollection(orgId, 'assets').catch(() => []),
    listOrgCollection(orgId, 'suppliers').catch(() => []),
    listOrgCollection(orgId, 'customers').catch(() => []),
    listOrgCollection(orgId, 'incidents').catch(() => []),
    listOrgCollection(orgId, 'documents').catch(() => []),
    listOrgCollection(orgId, 'evidence').catch(() => []),
    listOrgCollection(orgId, 'audits').catch(() => []),
    listOrgCollection(orgId, 'management-reviews').catch(() => []),
    listOrgCollection(orgId, 'nonconformities').catch(() => []),
    readOrgFile(orgId, 'context.json').catch(() => null),
    readOrgFile(orgId, 'scope.json').catch(() => null),
    readOrgFile(orgId, 'people.json').catch(() => null),
    readOrgFile(orgId, `awareness/program-${currentYear}.json`).catch(() => null),
    readOrgFile(orgId, `competence/program-${currentYear}.json`).catch(() => null),
    listOrgCollection(orgId, 'metrics').catch(() => []),
    listOrgCollection(orgId, 'legal-register').catch(() => []),
  ]);

  const alerts = [];
  const push = a => { if (a) alerts.push(a); };

  // ── Inactive people index ─────────────────────────────────────────────────
  const inactivePeople = new Set();
  if (peopleRaw && Array.isArray(peopleRaw.people)) {
    for (const p of peopleRaw.people) {
      if (p.endDate || p.active === false) inactivePeople.add(p.id);
    }
  }

  const soaControls = soaRaw ? (soaRaw.controls || []) : [];

  // ── CRITICAL ──────────────────────────────────────────────────────────────

  // highRisksUntreated
  push(makeAlert('highRisksUntreated',
    risks.filter(r =>
      r.riskLevel === 'HIGH' &&
      r.treatmentActionStatus !== 'Completed' &&
      r.accepted !== true
    ).map(r => r.id)
  ));

  // gdprNotificationOverdue — statutory 72h window from detectedAt (GDPR Art. 33).
  // Uses hoursAgo per §7 "Regulatory-hour exception to the midnight-boundary rule".
  push(makeAlert('gdprNotificationOverdue',
    incidents.filter(i =>
      i.gdprNotificationRequired === true &&
      i.gdprNotifiedAt == null &&
      i.detectedAt && hoursAgo(i.detectedAt) > 72
    ).map(i => i.id)
  ));

  // nis2EarlyWarningOverdue — statutory 24h window from detectedAt (NIS2 Art. 23(a)).
  push(makeAlert('nis2EarlyWarningOverdue',
    incidents.filter(i =>
      i.nis2Applicable === true &&
      i.nis2EarlyWarningAt == null &&
      i.detectedAt && hoursAgo(i.detectedAt) > 24
    ).map(i => i.id)
  ));

  // nis2NotificationOverdue — statutory 72h window from detectedAt (NIS2 Art. 23(b)).
  push(makeAlert('nis2NotificationOverdue',
    incidents.filter(i =>
      i.nis2Applicable === true &&
      i.nis2NotificationAt == null &&
      i.detectedAt && hoursAgo(i.detectedAt) > 72
    ).map(i => i.id)
  ));

  // nis2FinalReportOverdue — 30d window measured from nis2NotificationAt per
  // NIS2 Art. 23(d): the final-report clock starts at notification submission,
  // not at detection. Does not fire until nis2NotificationAt is recorded.
  push(makeAlert('nis2FinalReportOverdue',
    incidents.filter(i =>
      i.nis2Applicable === true &&
      i.nis2FinalReportAt == null &&
      i.nis2NotificationAt && hoursAgo(i.nis2NotificationAt) > 30 * 24
    ).map(i => i.id)
  ));

  // majorNcFindingWithoutRecord
  const ncFindingIds = [];
  for (const aud of audits) {
    for (const f of (aud.findings || [])) {
      if ((f.type === 'Major NC' || f.type === 'Minor NC') && !f.nonconformityId) {
        ncFindingIds.push(f.id);
      }
    }
  }
  push(makeAlert('majorNcFindingWithoutRecord', ncFindingIds));

  // repeatFinding
  push(makeAlert('repeatFinding',
    nonconformities.filter(nc => nc.repeatFinding === true && nc.status !== 'Closed').map(nc => nc.id)
  ));

  // certificationExpired / certificationExpiring90 / certificationExpiring180
  // Mutually exclusive ladder; gated on effectiveCertificationStatus !== 'none'.
  if (org.recertificationDate && effectiveCertStatus !== 'none') {
    const certDaysUntil = daysBetween(new Date(org.recertificationDate), todayD);
    if (certDaysUntil < 0) {
      push(makeAlert('certificationExpired', [], 1));
    } else if (certDaysUntil <= 90) {
      push(makeAlert('certificationExpiring90', [], 1));
    } else if (certDaysUntil <= 180) {
      push(makeAlert('certificationExpiring180', [], 1));
    }
  }

  // ── HIGH ──────────────────────────────────────────────────────────────────

  // residualRiskNotAccepted — field-presence guard per §7. Sourced from Risk
  // records since L4 #7 collapsed RTP entries into the Risk schema. Scoped to
  // Modify/Share treatments: residual acceptance does not apply to Avoid (the
  // activity is removed) or Retain (the risk is accepted via the `accepted`
  // field), where residualRiskAccepted is persisted as false but is meaningless.
  push(makeAlert('residualRiskNotAccepted',
    risks.filter(r =>
      (r.treatmentOption === 'Modify' || r.treatmentOption === 'Share') &&
      'residualRiskAccepted' in r &&
      r.residualRiskAccepted === false &&
      r.treatmentActionStatus === 'Completed'
    ).map(r => r.id)
  ));

  // controlGapOnHighRisk — derive from Risk.linkedControlIds crossed with SoA
  // applicability and implementation status. SoA is a pure aggregate view; it
  // does not hold reverse links to risks, so the inverse traversal from Risk
  // is the only sound source.
  const notImplementedApplicableControlIds = new Set(
    soaControls
      .filter(c => c.applicable && c.implementationStatus === 'Not implemented')
      .map(c => c.id)
  );
  const gapControlIds = new Set();
  for (const r of risks) {
    if (r.riskLevel !== 'HIGH') continue;
    if (!Array.isArray(r.linkedControlIds)) continue;
    for (const cid of r.linkedControlIds) {
      if (notImplementedApplicableControlIds.has(cid)) gapControlIds.add(cid);
    }
  }
  push(makeAlert('controlGapOnHighRisk', Array.from(gapControlIds)));

  // overdueRtpActions — sourced from Risk records since L4 #7
  push(makeAlert('overdueRtpActions',
    risks.filter(r =>
      r.treatmentDeadline &&
      new Date(r.treatmentDeadline) < todayD &&
      r.treatmentActionStatus !== 'Completed'
    ).map(r => r.id)
  ));

  // treatmentDeadlineMissing
  push(makeAlert('treatmentDeadlineMissing',
    risks.filter(r =>
      r.treatmentOption && r.treatmentOption !== 'Retain' && r.treatmentDeadline == null
    ).map(r => r.id)
  ));

  // openNcsOverdue
  push(makeAlert('openNcsOverdue',
    nonconformities.filter(nc =>
      nc.status !== 'Closed' &&
      Array.isArray(nc.correctiveActions) &&
      nc.correctiveActions.some(ca => ca.deadline && new Date(ca.deadline) < todayD)
    ).map(nc => nc.id)
  ));

  // ncClosedWithoutVerifier
  push(makeAlert('ncClosedWithoutVerifier',
    nonconformities.filter(nc =>
      nc.status === 'Closed' && (!nc.verifiedBy || nc.verifiedBy === '')
    ).map(nc => nc.id)
  ));

  // policyNotApproved — gated on ISMS intent
  if (
    effectiveCertStatus !== 'none' &&
    !documents.some(d => d.isInformationSecurityPolicy === true && d.status === 'Approved' && d.approvalDate)
  ) {
    push(makeAlert('policyNotApproved', [], 1));
  }

  // policyApprovedWithoutEvidence — ISO 27001 clause 5.2 imposes the formal
  // approval-evidence requirement on the Information Security Policy only.
  // Topic-specific policies are out of scope; their approvalEvidenceId field
  // is informational. Singleton enforced by PolicyView entry-point + §11 rule 40.
  push(makeAlert('policyApprovedWithoutEvidence',
    documents.filter(d =>
      d.isInformationSecurityPolicy === true && d.approvalDate &&
      'approvalEvidenceId' in d && d.approvalEvidenceId == null
    ).map(d => d.id)
  ));

  // Approved document without approver or approval date — data integrity rule
  // that fires when a Document is in status="Approved" but the approval metadata
  // required by ISO 27001 clauses 7.5.2/7.5.3 is incomplete. Uniform across
  // every type (Policy, Procedure, Standard, Guideline, Record, Template,
  // Other) and the ISP is not excluded. The form-level validation in
  // DocumentForm prevents new/edited records from reaching this state; this
  // alert is the detection lane for legacy and imported records.
  push(makeAlert('documentApprovedWithoutApproverOrDate',
    documents.filter(d =>
      d.status === 'Approved' && (
        !Array.isArray(d.approvedBy) || d.approvedBy.length === 0 ||
        d.approvalDate == null || d.approvalDate === ''
      )
    ).map(d => d.id)
  ));

  // policyOverdueForReview
  push(makeAlert('policyOverdueForReview',
    documents.filter(d =>
      d.type === 'Policy' && d.nextReviewDate && new Date(d.nextReviewDate) < todayD
    ).map(d => d.id)
  ));

  // soaNotApproved / rtpNotApproved / scopeNotApproved
  if (soaRaw && !soaRaw.approvalDate)   push(makeAlert('soaNotApproved', [], 1));
  if (rtpRaw && !rtpRaw.approvalDate)   push(makeAlert('rtpNotApproved', [], 1));
  if (scopeRaw && !scopeRaw.approvalDate) push(makeAlert('scopeNotApproved', [], 1));

  // soaApprovedWithoutEvidence / rtpApprovedWithoutEvidence / scopeApprovedWithoutEvidence
  if (soaRaw && soaRaw.approvalDate && 'approvalEvidenceId' in soaRaw && soaRaw.approvalEvidenceId == null)
    push(makeAlert('soaApprovedWithoutEvidence', [], 1));
  if (rtpRaw && rtpRaw.approvalDate && 'approvalEvidenceId' in rtpRaw && rtpRaw.approvalEvidenceId == null)
    push(makeAlert('rtpApprovedWithoutEvidence', [], 1));
  if (scopeRaw && scopeRaw.approvalDate && 'approvalEvidenceId' in scopeRaw && scopeRaw.approvalEvidenceId == null)
    push(makeAlert('scopeApprovedWithoutEvidence', [], 1));

  // topManagementCommitmentMissing — gated on ISMS intent
  if (org.topManagementCommitmentEvidenceId == null && effectiveCertStatus !== 'none') {
    push(makeAlert('topManagementCommitmentMissing', [], 1));
  }

  // noInternalAuditWithinCycle — gated on ISMS intent
  // Calendar-month cutoff per §7 "Frontier semantics".
  const auditCutoff = addMonths(todayD, -(org.auditFrequencyMonths || DEFAULT_AUDIT_CYCLE_MONTHS));
  if (
    effectiveCertStatus !== 'none' &&
    !audits.some(a =>
      a.type === 'Internal' && a.status === 'Completed' &&
      a.actualDate && new Date(a.actualDate) >= auditCutoff
    )
  ) {
    push(makeAlert('noInternalAuditWithinCycle', [], 1));
  }

  // noManagementReviewWithinCycle — gated on ISMS intent
  // Calendar-month cutoff per §7 "Frontier semantics".
  const mrCutoff = addMonths(todayD, -(org.mrFrequencyMonths || DEFAULT_MR_CYCLE_MONTHS));
  if (
    effectiveCertStatus !== 'none' &&
    !mrs.some(mr => mr.approvalDate && mr.date && new Date(mr.date) >= mrCutoff)
  ) {
    push(makeAlert('noManagementReviewWithinCycle', [], 1));
  }

  // MR-specific alerts — operate on the latest completed MR
  const latestMR = mrs
    .filter(mr => mr.approvalDate && mr.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

  if (latestMR) {
    if (latestMR.topManagementPresent === false)
      push(makeAlert('mrWithoutTopManagement', [latestMR.id]));

    const REQUIRED_INPUTS = [
      'previousActionsStatus', 'externalInternalChanges', 'interestedPartiesChanges',
      'interestedPartiesFeedback', 'riskTreatmentStatus', 'improvementOpportunities',
      'climateChangeConsiderations',
    ];
    const REQUIRED_PERF_FIELDS = ['nonconformitiesTrend', 'monitoringResults', 'auditResults', 'objectivesFulfilment'];
    const inp = latestMR.inputs || {};
    const pf  = inp.performanceFeedback || {};
    const hasEmptyInput =
      REQUIRED_INPUTS.some(k => !inp[k] || inp[k].trim() === '') ||
      REQUIRED_PERF_FIELDS.some(k => !pf[k] || pf[k].trim() === '');
    if (hasEmptyInput) push(makeAlert('mrWithEmptyRequiredInput', [latestMR.id]));

    if (!Array.isArray(latestMR.decisions) || latestMR.decisions.length === 0) {
      push(makeAlert('mrWithoutDecisions', [latestMR.id]));
    } else if (!Array.isArray(latestMR.actions) || latestMR.actions.length === 0) {
      push(makeAlert('mrWithDecisionsButNoActions', [latestMR.id]));
    }
  }

  // supplierGdprGap
  push(makeAlert('supplierGdprGap',
    suppliers.filter(s => s.personalDataProcessed === true && s.dpaSigned === false).map(s => s.id)
  ));

  // customerGdprGap — mirror of supplierGdprGap for the Customer module.
  push(makeAlert('customerGdprGap',
    customers.filter(c => c.processesPersonalData === true && c.dpaSigned === false && c.status === 'Active').map(c => c.id)
  ));

  // incidentsWithoutNc — 7-day grace from detectedAt
  push(makeAlert('incidentsWithoutNc',
    incidents.filter(i =>
      (i.severity === 'Critical' || i.severity === 'High') &&
      (!Array.isArray(i.linkedNonconformityIds) || i.linkedNonconformityIds.length === 0) &&
      i.detectedAt && daysAgo(i.detectedAt) > 7
    ).map(i => i.id)
  ));

  // riskAcceptedWithoutApprover
  push(makeAlert('riskAcceptedWithoutApprover',
    risks.filter(r => r.accepted === true && (!r.acceptedBy || r.acceptedBy === '')).map(r => r.id)
  ));

  // personInactiveInOwnerRoles — scans only the live-accountability subset of §4
  // "Person-reference fields by entity": fields representing an ongoing
  // responsibility left without an active holder (owner / custodian / coordinator
  // / responsible / role-membership / ISMS-role / consultant). Historical
  // point-in-time fields (approvedBy, identifiedBy, acceptedBy, collectedBy,
  // reportedBy, auditor, chairperson, attendees, verifiedBy) are intentionally
  // excluded — a person who was active when they approved/collected/reported
  // remains a valid historical record after going inactive, so flagging them
  // produces false positives. §4 stays the full source of truth for off-boarding,
  // exports and bulk reassignments; only this dashboard alert is narrowed.
  // Capture (inactive person, affected entity) pairs instead of a flat Set of
  // entity IDs, deduplicated by `${personId}::${entityId}` so the same person
  // referenced in several fields of one entity yields a single pair. chkOwner /
  // chkOwnerArr keep their (entityId, val) / (entityId, arr) signatures.
  const inactivePairs = new Map();
  const chkOwner = (entityId, val) => {
    if (typeof val === 'string' && val.startsWith('person-') && inactivePeople.has(val)) {
      const pairKey = `${val}::${entityId}`;
      if (!inactivePairs.has(pairKey)) inactivePairs.set(pairKey, { personId: val, entityId });
    }
  };
  const chkOwnerArr = (entityId, arr) => {
    if (Array.isArray(arr)) for (const v of arr) chkOwner(entityId, v);
  };

  // Organization (profile)
  const orgEntityId = org.id || 'organization';
  chkOwner(orgEntityId, org.consultantPersonId);
  const ismsRoles = org.ismsRoles || {};
  const _tms = ismsRoles.topManagementSignatory;
  chkOwnerArr(orgEntityId, Array.isArray(_tms) ? _tms : (typeof _tms === 'string' && _tms ? [_tms] : []));
  chkOwner(orgEntityId, ismsRoles.ismsManager);
  chkOwner(orgEntityId, ismsRoles.ciso);
  chkOwner(orgEntityId, ismsRoles.dpo);
  chkOwner(orgEntityId, ismsRoles.nis2Contact);

  // Risk
  for (const r of risks) {
    chkOwner(r.id, r.owner);
    chkOwnerArr(r.id, Array.isArray(r.treatmentActionOwner)
      ? r.treatmentActionOwner
      : (typeof r.treatmentActionOwner === 'string' && r.treatmentActionOwner ? [r.treatmentActionOwner] : []));
  }

  // SoA
  for (const c of soaControls) chkOwner(c.id, c.owner);

  // RTP
  // (no live-accountability person fields; root approvedBy is point-in-time)

  // Objective
  for (const o of objectives) {
    chkOwner(o.id, o.owner);
  }

  // Asset
  for (const a of assets) {
    chkOwner(a.id, a.owner);
    chkOwner(a.id, a.custodian);
  }

  // Incident — incidentCoordinator (person-NNN); chkOwner guards the prefix
  for (const i of incidents) {
    chkOwner(i.id, i.incidentCoordinator);
  }

  // Document
  for (const d of documents) {
    chkOwner(d.id, d.owner);
  }

  // CompetenceProgram
  if (competenceRaw) {
    for (const role of (competenceRaw.roles || [])) {
      chkOwnerArr('competence', role.personIds);
      for (const ta of (role.trainingActions || [])) chkOwner('competence', ta.responsible);
    }
  }

  // Metric
  for (const m of metrics) chkOwner(m.id, m.responsible);

  // ManagementReview
  for (const mr of mrs) {
    for (const dec of (mr.decisions || [])) chkOwner(mr.id, dec.ownerId);
    for (const act of (mr.actions || [])) chkOwner(mr.id, act.responsible);
  }

  // Nonconformity
  for (const nc of nonconformities) {
    chkOwner(nc.id, nc.owner);
    for (const corr of (nc.corrections || [])) chkOwner(nc.id, corr.responsible);
    for (const ca of (nc.correctiveActions || [])) chkOwnerArr(nc.id, ca.responsible);
  }

  // LegalRegister
  for (const lr of legalRegister) chkOwner(lr.id, lr.responsiblePerson);

  // personInactiveInOwnerRoles breakdown — one case per captured (inactive
  // person, affected entity) pair, so the header "(N)" matches the rows. The
  // alert keeps its no-entityType catalog shape, so the generic enrichment below
  // skips it; this dedicated breakdown is the only place its cases attach. Each
  // case navigates to the affected entity's own module (documents/risks open the
  // detail by id today; other collection views open their list until they consume
  // editId), with org / SoA / RTP / competence routing to their own destination
  // and evidence / unresolved ids falling back to Settings → People. Names and
  // entity labels are resolved inline (no component imports).
  const inactivePairList = [...inactivePairs.values()];
  const personInactiveAlert = makeAlert('personInactiveInOwnerRoles', [], inactivePairList.length);
  if (personInactiveAlert) {
    const personNameById = new Map();
    if (peopleRaw && Array.isArray(peopleRaw.people)) {
      for (const p of peopleRaw.people) personNameById.set(p.id, p.name);
    }
    // Index every collection entity by id, tagging each with the module its
    // CaseRow navigates to. Evidence is intentionally excluded — it has no
    // navigable module — so an evidence-sourced pair falls through to the safe
    // Settings → People fallback in entityDestination below.
    const entityRecordById = new Map();
    for (const [coll, module] of [
      [documents, 'documents'], [risks, 'risks'], [objectives, 'objectives'],
      [assets, 'assets'], [incidents, 'incidents'], [metrics, 'metrics'],
      [audits, 'audits'], [mrs, 'management-reviews'],
      [nonconformities, 'nonconformities'], [legalRegister, 'legal-register'],
      [soaControls, 'soa'],
    ]) {
      if (Array.isArray(coll)) for (const rec of coll) {
        if (rec && rec.id != null) entityRecordById.set(rec.id, { record: rec, module });
      }
    }
    // Readable label: prefer a human name field (title / name / objective /
    // category), demote ref to the penultimate fallback, raw id last.
    const entityLabel = (entityId) => {
      if (entityId === orgEntityId) return org.name || 'Organization';
      if (entityId === 'soa') return 'SoA';
      if (entityId === 'rtp') return 'RTP';
      if (entityId === 'competence') return 'Competence program';
      const entry = entityRecordById.get(entityId);
      if (!entry) return entityId;
      const rec = entry.record;
      return rec.title || rec.name || rec.objective || rec.category || rec.ref || entityId;
    };
    // Per-entity destination: open the entity's own module (documents/risks open
    // the detail via editId today; other collection views open their list until
    // they consume editId). org / SoA / RTP / competence route to their own view
    // ({} is intentionally truthy so CaseRow skips the editId fallback); evidence
    // and unresolved ids keep the Settings → People fallback.
    const entityDestination = (entityId) => {
      if (entityId === orgEntityId) return { module: 'settings', navParams: { section: 'people' } };
      if (entityId === 'soa') return { module: 'soa', navParams: {} };
      if (entityId === 'rtp') return { module: 'rtp', navParams: {} };
      if (entityId === 'competence') return { module: 'competence', navParams: {} };
      const entry = entityRecordById.get(entityId);
      if (entry) return { module: entry.module, navParams: { editId: entityId } };
      return { module: 'settings', navParams: { section: 'people' } };
    };
    personInactiveAlert.cases = inactivePairList.map(({ personId, entityId }) => {
      const { module, navParams } = entityDestination(entityId);
      return {
        id: `${personId}::${entityId}`,
        module,
        navParams,
        label: `${personNameById.get(personId) || personId} — ${entityLabel(entityId)}`,
      };
    });
  }
  push(personInactiveAlert);

  // ── MEDIUM ────────────────────────────────────────────────────────────────

  // contextReviewOverdue
  if (contextRaw && contextRaw.reviewDate) {
    const ctxCutoff = new Date(contextRaw.reviewDate);
    ctxCutoff.setMonth(ctxCutoff.getMonth() + 12);
    if (ctxCutoff < todayD) push(makeAlert('contextReviewOverdue', [], 1));
  }

  // documentsOverdueForReview (excl. Policy)
  push(makeAlert('documentsOverdueForReview',
    documents.filter(d =>
      d.type !== 'Policy' && d.status === 'Approved' &&
      d.nextReviewDate && new Date(d.nextReviewDate) < todayD
    ).map(d => d.id)
  ));

  // legalRegisterReviewOverdue
  push(makeAlert('legalRegisterReviewOverdue',
    legalRegister.filter(lr => lr.reviewDate && new Date(lr.reviewDate) < todayD).map(lr => lr.id)
  ));

  // objectivesAtRisk
  push(makeAlert('objectivesAtRisk',
    objectives.filter(o => o.status === 'At Risk' || o.status === 'Not Achieved').map(o => o.id)
  ));

  // objectivesDeadlineOverdue — both Achieved and Not Achieved are terminal
  push(makeAlert('objectivesDeadlineOverdue',
    objectives.filter(o =>
      o.deadline && new Date(o.deadline) < todayD &&
      o.status !== 'Achieved' && o.status !== 'Not Achieved'
    ).map(o => o.id)
  ));

  // objectivesWithoutMeasurement
  push(makeAlert('objectivesWithoutMeasurement',
    objectives.filter(o =>
      (!Array.isArray(o.linkedMetricIds) || o.linkedMetricIds.length === 0) &&
      (!o.measurementMethod || o.measurementMethod === '')
    ).map(o => o.id)
  ));

  // supplierReviewsOverdue
  push(makeAlert('supplierReviewsOverdue',
    suppliers.filter(s => s.nextReviewDate && new Date(s.nextReviewDate) < todayD).map(s => s.id)
  ));

  // supplierCriticalWithoutExitPlan — only fire when exitPlanExists field is present
  push(makeAlert('supplierCriticalWithoutExitPlan',
    suppliers.filter(s =>
      s.criticality === 'Critical' &&
      'exitPlanExists' in s && s.exitPlanExists === false
    ).map(s => s.id)
  ));

  // supplierCriticalWithExpiredContract
  push(makeAlert('supplierCriticalWithExpiredContract',
    suppliers.filter(s =>
      s.criticality === 'Critical' && s.contractExpiry && new Date(s.contractExpiry) < todayD
    ).map(s => s.id)
  ));

  // incidentRootCauseMissing
  push(makeAlert('incidentRootCauseMissing',
    incidents.filter(i =>
      (i.severity === 'Critical' || i.severity === 'High') &&
      (!i.rootCause || i.rootCause === '') &&
      i.detectedAt && daysAgo(i.detectedAt) > 30
    ).map(i => i.id)
  ));

  // ncWithoutRootCauses
  push(makeAlert('ncWithoutRootCauses',
    nonconformities.filter(nc =>
      (nc.status === 'In Progress' || nc.status === 'Closed') &&
      (!Array.isArray(nc.rootCauses) || nc.rootCauses.length === 0)
    ).map(nc => nc.id)
  ));

  // climateChangeNotAssessed
  if (contextRaw && contextRaw.climateChangeRelevant == null) {
    push(makeAlert('climateChangeNotAssessed', [], 1));
  }

  // riskHighWithoutOwner
  push(makeAlert('riskHighWithoutOwner',
    risks.filter(r => r.riskLevel === 'HIGH' && (!r.owner || r.owner === '')).map(r => r.id)
  ));

  // riskReviewOverdue
  push(makeAlert('riskReviewOverdue',
    risks.filter(r => r.nextReviewDate && new Date(r.nextReviewDate) < todayD).map(r => r.id)
  ));

  // metricReadingOverdue
  {
    const overdueMetricIds = [];
    for (const met of metrics) {
      const grace = getGraceDays(met.frequency);
      const readings = Array.isArray(met.readings) ? met.readings : [];
      let expectedNext;
      if (readings.length === 0) {
        const base = (met.updatedAt || '').slice(0, 10) || todayD.toISOString().slice(0, 10);
        if (met.frequency === 'Weekly') {
          expectedNext = new Date(new Date(base).getTime() + 7 * MS_PER_DAY);
        } else {
          const periods = { Monthly: 1, Quarterly: 3, Annual: 12 };
          expectedNext = addMonths(base, periods[met.frequency] || 1);
        }
      } else {
        const sorted = readings.slice().sort((a, b) => new Date(b.date) - new Date(a.date));
        const lastDate = sorted[0].date;
        if (met.frequency === 'Weekly') {
          expectedNext = new Date(new Date(lastDate).getTime() + 7 * MS_PER_DAY);
        } else {
          const periods = { Monthly: 1, Quarterly: 3, Annual: 12 };
          expectedNext = addMonths(lastDate, periods[met.frequency] || 1);
        }
      }
      const deadline = new Date(expectedNext.getTime() + grace * MS_PER_DAY);
      if (todayD > deadline) overdueMetricIds.push(met.id);
    }
    push(makeAlert('metricReadingOverdue', overdueMetricIds));
  }

  // awarenessCompetenceNoProgramme — gated on ISMS intent
  if (effectiveCertStatus !== 'none' && (!awarenessRaw || !competenceRaw)) {
    push(makeAlert('awarenessCompetenceNoProgramme', [], 1));
  }

  // awarenessCoverageBelowTarget — gated on ISMS intent and policy presence
  if (org.awarenessCoveragePolicy && awarenessRaw) {
    const policy = org.awarenessCoveragePolicy;
    const target = policy.target;
    const acts = Array.isArray(awarenessRaw.activities) ? awarenessRaw.activities : [];
    const completedWithCov = acts.filter(a => a.status === 'Completed' && a.coveragePercent != null);
    if (completedWithCov.length > 0) {
      const avgCoverage = Math.round(completedWithCov.reduce((s, a) => s + a.coveragePercent, 0) / completedWithCov.length * 10) / 10;
      if (avgCoverage < target) {
        push(makeAlert('awarenessCoverageBelowTarget', [], 1));
      }
    }
  }

  // certificationStatusStale
  if (
    effectiveCertStatus === 'certified' &&
    org.recertificationDate &&
    new Date(org.recertificationDate) < todayD
  ) {
    push(makeAlert('certificationStatusStale', [], 1));
  }

  // evidenceRetentionExpiring (set AND within 90 days, not yet expired)
  const evNinetyDaysOut = new Date(todayD.getTime() + 90 * MS_PER_DAY);
  push(makeAlert('evidenceRetentionExpiring',
    evidence.filter(e =>
      e.retentionEndDate &&
      new Date(e.retentionEndDate) >= todayD &&
      new Date(e.retentionEndDate) <= evNinetyDaysOut
    ).map(e => e.id)
  ));

  // stage1ImminentWithGaps
  if (org.stage1AuditDate) {
    const s1DaysUntil = daysBetween(new Date(org.stage1AuditDate), todayD);
    if (s1DaysUntil >= 0 && s1DaysUntil <= 30) {
      const s1PolicyOk = documents.some(d => d.isInformationSecurityPolicy === true && d.status === 'Approved' && d.approvalDate);
      const s1SoaOk    = soaRaw   && soaRaw.approvalDate;
      const s1RtpOk    = rtpRaw   && rtpRaw.approvalDate;
      const s1ScopeOk  = scopeRaw && scopeRaw.approvalDate;
      if (!s1PolicyOk || !s1SoaOk || !s1RtpOk || !s1ScopeOk) {
        push(makeAlert('stage1ImminentWithGaps', [], 1));
      }
    }
  }

  // ── LOW ───────────────────────────────────────────────────────────────────

  // scopeOutsourcedMissingSupplier
  if (scopeRaw && Array.isArray(scopeRaw.outsourcedProcesses)) {
    if (scopeRaw.outsourcedProcesses.some(
      p => !Array.isArray(p.linkedSupplierIds) || p.linkedSupplierIds.length === 0
    )) {
      push(makeAlert('scopeOutsourcedMissingSupplier', [], 1));
    }
  }

  // aiProcessingWithoutConsent
  if (org.aiProcessingConsent === false) {
    push(makeAlert('aiProcessingWithoutConsent', [], 1));
  }

  // ── Multi-entity breakdown (clean entity types) ───────────────────────────
  // Post-process: for every alert whose catalog entry declares an `entityType`,
  // resolve each entityId to a navigable case { id, label, module } so the
  // Dashboard can render an inline breakdown. Per-collection lookup indices are
  // built the same way computeOrphanRefs builds its id Sets (here a Map from id
  // to record so the label can be composed). Alerts without an `entityType` are
  // left exactly as they are — no `cases` field is attached. The `module` is
  // derived from the entityType, not the catalog `module` (e.g. policy* alerts
  // navigate cases to the Documents view). makeAlert, entityIds, severity,
  // count, ordering, gating and predicates are untouched; `cases` is additive.
  const ENTITY_TYPE_TO_MODULE = {
    risk: 'risks',
    incident: 'incidents',
    nonconformity: 'nonconformities',
    document: 'documents',
    supplier: 'suppliers',
    customer: 'customers',
    objective: 'objectives',
    'legal-register': 'legal-register',
    metric: 'metrics',
  };

  const indexById = items => new Map((Array.isArray(items) ? items : []).map(it => [it.id, it]));
  const entityIndex = {
    risk: indexById(risks),
    incident: indexById(incidents),
    nonconformity: indexById(nonconformities),
    document: indexById(documents),
    supplier: indexById(suppliers),
    customer: indexById(customers),
    objective: indexById(objectives),
    'legal-register': indexById(legalRegister),
    metric: indexById(metrics),
  };

  // Composes the per-type label. For ref-bearing types: "ref - descriptive";
  // when ref is empty fall back to the id in the ref position; when the
  // descriptive field is empty render just the ref/id. Document has no ref:
  // title only, falling back to the id when title is empty. An unresolved id
  // (stale) yields the id itself as label.
  const trimmed = v => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
  function composeCaseLabel(entityType, rec, id) {
    if (!rec) return id;
    const refPart = (ref) => trimmed(ref) || id;
    const join = (ref, desc) => {
      const d = trimmed(desc);
      return d ? `${refPart(ref)} - ${d}` : refPart(ref);
    };
    switch (entityType) {
      case 'risk':           return join(rec.ref, rec.riskSummary);
      case 'incident':       return join(rec.ref, rec.title);
      case 'objective':      return join(rec.ref, rec.objective);
      case 'legal-register': return join(rec.ref, rec.title);
      case 'metric':         return join(rec.ref, rec.name);
      case 'supplier':       return join(rec.ref, rec.name);
      case 'customer':       return join(rec.ref, rec.name);
      case 'nonconformity':  return join(rec.ref, rec.category);
      case 'document':       return trimmed(rec.title) || id;
      default:               return id;
    }
  }

  for (const alert of alerts) {
    const entityType = (ALERT_CATALOG[alert.key] || {}).entityType;
    if (!entityType) continue;
    const index = entityIndex[entityType];
    const viewModule = ENTITY_TYPE_TO_MODULE[entityType];
    alert.cases = (alert.entityIds || []).map(id => ({
      id,
      module: viewModule,
      label: composeCaseLabel(entityType, index ? index.get(id) : null, id),
    }));
  }

  return alerts;
}

// ─── computeRiskGaps ──────────────────────────────────────────────────────────
// Evaluates the six Risk gap predicates declared in §7 "Gap detection" and
// returns one entry per detected gap. Dashboard.js renders the result inline;
// no inline predicates live in the component layer.
export async function computeRiskGaps(orgId) {
  const [risks, soaRaw] = await Promise.all([
    listOrgCollection(orgId, 'risks').catch(() => []),
    readOrgFile(orgId, 'controls/soa.json').catch(() => null),
  ]);
  const soaControls = soaRaw ? (soaRaw.controls || []) : [];
  const controlById = new Map(soaControls.map(c => [c.id, c]));

  const gaps = [];
  for (const r of risks) {
    const linked = Array.isArray(r.linkedControlIds) ? r.linkedControlIds : [];

    if (r.riskLevel === 'HIGH' && linked.length > 0) {
      const allNotImpl = linked.every(id => {
        const c = controlById.get(id);
        return c && c.implementationStatus === 'Not implemented';
      });
      if (allNotImpl) gaps.push({ riskId: r.id, gap: 'highRiskAllControlsNotImplemented', riskSummary: r.riskSummary, riskRef: r.ref || r.id });
    }

    if (r.treatmentOption === 'Modify' && linked.length === 0) {
      gaps.push({ riskId: r.id, gap: 'modifyWithoutLinkedControls', riskSummary: r.riskSummary, riskRef: r.ref || r.id });
    }

    if (r.treatmentActionStatus === 'Completed' && r.residualRiskLevel === 'HIGH') {
      gaps.push({ riskId: r.id, gap: 'treatmentCompletedButResidualHigh', riskSummary: r.riskSummary, riskRef: r.ref || r.id });
    }

    if (typeof r.residualScore === 'number' &&
        typeof r.inherentScore === 'number' &&
        r.residualScore > r.inherentScore) {
      gaps.push({ riskId: r.id, gap: 'residualExceedsInherent', riskSummary: r.riskSummary, riskRef: r.ref || r.id });
    }

    if (r.accepted === true &&
        (typeof r.acceptanceCriteria !== 'string' || r.acceptanceCriteria.trim() === '')) {
      gaps.push({ riskId: r.id, gap: 'acceptedWithoutCriteria', riskSummary: r.riskSummary, riskRef: r.ref || r.id });
    }

    // Gap 6 — segregation of duties (A.5.3 / 6.1.3f). Approver-equals-owner is
    // allowed at save time (advisory notice only in RiskForm, not a validation
    // error) and is reported as a gap only when no exception justification has
    // been recorded. A non-empty sodExceptionJustification documents why the
    // approver coincides with the owner and what compensating controls apply,
    // which silences the gap — mirroring how acceptanceCriteria silences the
    // acceptedWithoutCriteria gap above.
    if (r.treatmentApprovedBy && r.owner && r.treatmentApprovedBy === r.owner &&
        (typeof r.sodExceptionJustification !== 'string' || r.sodExceptionJustification.trim() === '')) {
      gaps.push({ riskId: r.id, gap: 'approverEqualsOwner', riskSummary: r.riskSummary, riskRef: r.ref || r.id });
    }
  }

  return gaps;
}

// ─── computeDocumentGaps ──────────────────────────────────────────────────────
// Evaluates the Document gap predicates surfaced as a no-impact-on-scores lane
// on the Dashboard, parallel to computeRiskGaps. Two predicates fire per
// document, both restricted to status="Approved" and to the Policy/Procedure/
// Standard/Guideline tier (Records, Templates, Other never surface):
//   - approvedWithoutEvidence: approvalDate set, isInformationSecurityPolicy
//     !== true (the ISP is excluded — its missing evidence already triggers
//     the high-severity policyApprovedWithoutEvidence alert), field-presence
//     guard 'approvalEvidenceId' in d, and approvalEvidenceId == null.
//   - orphanNoClauseRefsNoControls: clauseRefs missing/empty AND
//     linkedControlIds missing/empty. The ISP is NOT excluded — an Approved
//     ISP without ISO clause or control mapping is a legitimate orphan.
// A document can produce multiple entries. Self-contained loading mirrors
// computeRiskGaps's pattern.
export async function computeDocumentGaps(orgId) {
  const documents = await listOrgCollection(orgId, 'documents').catch(() => []);

  const TYPES = ['Policy', 'Procedure', 'Standard', 'Guideline'];
  const gaps = [];

  for (const d of documents) {
    if (!TYPES.includes(d.type)) continue;
    if (d.status !== 'Approved') continue;

    if (
      d.approvalDate &&
      d.isInformationSecurityPolicy !== true &&
      'approvalEvidenceId' in d &&
      d.approvalEvidenceId == null
    ) {
      gaps.push({ documentId: d.id, gap: 'approvedWithoutEvidence', documentTitle: d.title });
    }

    const clauseRefs = Array.isArray(d.clauseRefs) ? d.clauseRefs : [];
    const linkedControlIds = Array.isArray(d.linkedControlIds) ? d.linkedControlIds : [];
    if (clauseRefs.length === 0 && linkedControlIds.length === 0) {
      gaps.push({ documentId: d.id, gap: 'orphanNoClauseRefsNoControls', documentTitle: d.title });
    }
  }

  return gaps;
}

// ─── computeOrphanRefs ────────────────────────────────────────────────────────
// §7 "Orphan reference detection". Loads every collection once, builds an ID
// set per collection, then scans every cross-reference field listed in the
// §7 scenario table for stale IDs. Returns one entry per (holder, field) pair
// where stale IDs were found; multiple stale values inside the same field on
// the same holder collapse into a single entry per the §7 contract.
export async function computeOrphanRefs(orgId) {
  const [
    risks, assets, suppliers, customers, incidents, audits,
    nonconformities, objectives, metrics, documents,
    mrs, legalRegister, authorities, scopeRaw,
  ] = await Promise.all([
    listOrgCollection(orgId, 'risks').catch(() => []),
    listOrgCollection(orgId, 'assets').catch(() => []),
    listOrgCollection(orgId, 'suppliers').catch(() => []),
    listOrgCollection(orgId, 'customers').catch(() => []),
    listOrgCollection(orgId, 'incidents').catch(() => []),
    listOrgCollection(orgId, 'audits').catch(() => []),
    listOrgCollection(orgId, 'nonconformities').catch(() => []),
    listOrgCollection(orgId, 'objectives').catch(() => []),
    listOrgCollection(orgId, 'metrics').catch(() => []),
    listOrgCollection(orgId, 'documents').catch(() => []),
    listOrgCollection(orgId, 'management-reviews').catch(() => []),
    listOrgCollection(orgId, 'legal-register').catch(() => []),
    listOrgCollection(orgId, 'authorities').catch(() => []),
    readOrgFile(orgId, 'scope.json').catch(() => null),
  ]);

  const riskIds      = new Set(risks.map(r => r.id));
  const assetIds     = new Set(assets.map(r => r.id));
  const supplierIds  = new Set(suppliers.map(r => r.id));
  const customerIds  = new Set(customers.map(r => r.id));
  const incidentIds  = new Set(incidents.map(r => r.id));
  const auditIds     = new Set(audits.map(r => r.id));
  const ncIds        = new Set(nonconformities.map(r => r.id));
  const objectiveIds = new Set(objectives.map(r => r.id));
  const metricIds    = new Set(metrics.map(r => r.id));
  const documentIds  = new Set(documents.map(r => r.id));
  const legalIds     = new Set(legalRegister.map(r => r.id));
  const authorityIds = new Set(authorities.map(r => r.id));

  const results = [];

  function scanArray(holder, holderType, holderId, holderRef, field, validSet, targetType) {
    if (!Array.isArray(holder[field])) return;
    const orphanIds = holder[field].filter(id => !validSet.has(id));
    if (orphanIds.length === 0) return;
    results.push({ targetType, holderType, holderId, holderRef, field, orphanIds });
  }

  function scanSingle(holder, holderType, holderId, holderRef, field, validSet, targetType) {
    const v = holder[field];
    if (typeof v !== 'string' || v === '') return;
    if (validSet.has(v)) return;
    results.push({ targetType, holderType, holderId, holderRef, field, orphanIds: [v] });
  }

  // ── Asset holder rows (2 scans) ─────────────────────────────────────────
  for (const a of assets) {
    const ref = a.ref || a.id;
    scanArray(a, 'asset', a.id, ref, 'linkedSupplierIds', supplierIds, 'supplier');
    scanArray(a, 'asset', a.id, ref, 'linkedDocumentIds', documentIds, 'document');
  }

  // ── Incident holder rows (6 scans) ──────────────────────────────────────
  for (const i of incidents) {
    const ref = i.ref || i.id;
    scanArray(i, 'incident', i.id, ref, 'linkedAssetIds',         assetIds,     'asset');
    scanArray(i, 'incident', i.id, ref, 'linkedSupplierIds',      supplierIds,  'supplier');
    scanArray(i, 'incident', i.id, ref, 'linkedNonconformityIds', ncIds,        'nonconformity');
    scanArray(i, 'incident', i.id, ref, 'linkedDocumentIds',      documentIds,  'document');
    scanArray(i, 'incident', i.id, ref, 'linkedLegalRegisterIds', legalIds,     'legal-register');
    scanArray(i, 'incident', i.id, ref, 'notifiedAuthorityIds',   authorityIds, 'authority');
  }

  // ── Objective holder rows (2 scans) ─────────────────────────────────────
  for (const o of objectives) {
    const ref = o.ref || o.id;
    scanArray(o, 'objective', o.id, ref, 'linkedRiskIds',   riskIds,   'risk');
    scanArray(o, 'objective', o.id, ref, 'linkedMetricIds', metricIds, 'metric');
  }

  // ── Audit holder rows (1 scan) ──────────────────────────────────────────
  for (const aud of audits) {
    const ref = aud.ref || aud.id;
    if (Array.isArray(aud.findings)) {
      const stale = [];
      for (const f of aud.findings) {
        const v = f.nonconformityId;
        if (typeof v === 'string' && v !== '' && !ncIds.has(v)) stale.push(v);
      }
      if (stale.length > 0) {
        results.push({
          targetType: 'nonconformity',
          holderType: 'audit',
          holderId: aud.id,
          holderRef: ref,
          field: 'findings[].nonconformityId',
          orphanIds: stale,
        });
      }
    }
  }

  // ── ManagementReview holder rows (6 scans) ──────────────────────────────
  for (const mr of mrs) {
    const ref = mr.ref || mr.id;
    scanArray(mr, 'management-review', mr.id, ref, 'linkedRiskIds',          riskIds,      'risk');
    scanArray(mr, 'management-review', mr.id, ref, 'linkedIncidentIds',      incidentIds,  'incident');
    scanArray(mr, 'management-review', mr.id, ref, 'linkedAuditIds',         auditIds,     'audit');
    scanArray(mr, 'management-review', mr.id, ref, 'linkedNonconformityIds', ncIds,        'nonconformity');
    scanArray(mr, 'management-review', mr.id, ref, 'linkedObjectiveIds',     objectiveIds, 'objective');
    scanArray(mr, 'management-review', mr.id, ref, 'linkedMetricIds',        metricIds,    'metric');
  }

  // ── Nonconformity holder rows (3 scans) ─────────────────────────────────
  for (const nc of nonconformities) {
    const ref = nc.ref || nc.id;
    scanArray (nc, 'nonconformity', nc.id, ref, 'linkedRiskIds',     riskIds,     'risk');
    scanArray (nc, 'nonconformity', nc.id, ref, 'linkedIncidentIds', incidentIds, 'incident');
    scanSingle(nc, 'nonconformity', nc.id, ref, 'auditId',           auditIds,    'audit');
  }

  // ── Risk holder rows (4 scans) ──────────────────────────────────────────
  for (const r of risks) {
    const ref = r.ref || r.id;
    scanArray(r, 'risk', r.id, ref, 'linkedAssetIds',         assetIds,    'asset');
    scanArray(r, 'risk', r.id, ref, 'linkedSupplierIds',      supplierIds, 'supplier');
    scanArray(r, 'risk', r.id, ref, 'linkedIncidentIds',      incidentIds, 'incident');
    scanArray(r, 'risk', r.id, ref, 'linkedNonconformityIds', ncIds,       'nonconformity');
  }

  // ── Metric holder rows (1 scan) ─────────────────────────────────────────
  for (const m of metrics) {
    const ref = m.ref || m.id;
    scanSingle(m, 'metric', m.id, ref, 'objectiveId', objectiveIds, 'objective');
  }

  // ── LegalRegister holder rows (3 scans) ─────────────────────────────────
  for (const lr of legalRegister) {
    const ref = lr.ref || lr.id;
    scanArray(lr, 'legal-register', lr.id, ref, 'linkedDocumentIds',     documentIds,  'document');
    scanArray(lr, 'legal-register', lr.id, ref, 'triggeringCustomerIds', customerIds,  'customer');
    scanArray(lr, 'legal-register', lr.id, ref, 'linkedAuthorityIds',    authorityIds, 'authority');
  }

  // ── Scope holder rows (3 scans) ─────────────────────────────────────────
  if (scopeRaw) {
    scanArray(scopeRaw, 'scope', 'scope', 'scope', 'linkedAssetIds',    assetIds,    'asset');
    scanArray(scopeRaw, 'scope', 'scope', 'scope', 'linkedSupplierIds', supplierIds, 'supplier');
    if (Array.isArray(scopeRaw.outsourcedProcesses)) {
      const stale = [];
      for (const p of scopeRaw.outsourcedProcesses) {
        if (Array.isArray(p.linkedSupplierIds)) {
          for (const sid of p.linkedSupplierIds) {
            if (!supplierIds.has(sid)) stale.push(sid);
          }
        }
      }
      if (stale.length > 0) {
        results.push({
          targetType: 'supplier',
          holderType: 'scope',
          holderId: 'scope',
          holderRef: 'scope',
          field: 'outsourcedProcesses[].linkedSupplierIds',
          orphanIds: stale,
        });
      }
    }
  }

  return results;
}

// ─── scanReferencesToEntity ───────────────────────────────────────────────────
// §11 rule 42 — inverse predicate of computeOrphanRefs over the §7 scenario
// table. Given a target entity (one of the 11 top-level types) and its id,
// returns every holder record/field that references the id. Used by list-view
// delete handlers to block hard-delete when references exist. Reads the same
// collections as computeOrphanRefs; missing or corrupt files are skipped
// silently. Never throws.
export async function scanReferencesToEntity(orgId, targetType, targetId) {
  const [
    risks, assets, incidents, audits,
    nonconformities, objectives, metrics,
    mrs, legalRegister, scopeRaw,
  ] = await Promise.all([
    listOrgCollection(orgId, 'risks').catch(() => []),
    listOrgCollection(orgId, 'assets').catch(() => []),
    listOrgCollection(orgId, 'incidents').catch(() => []),
    listOrgCollection(orgId, 'audits').catch(() => []),
    listOrgCollection(orgId, 'nonconformities').catch(() => []),
    listOrgCollection(orgId, 'objectives').catch(() => []),
    listOrgCollection(orgId, 'metrics').catch(() => []),
    listOrgCollection(orgId, 'management-reviews').catch(() => []),
    listOrgCollection(orgId, 'legal-register').catch(() => []),
    readOrgFile(orgId, 'scope.json').catch(() => null),
  ]);

  const hits = [];

  function pushArray(holder, holderType, holderId, holderRef, field) {
    if (Array.isArray(holder[field]) && holder[field].includes(targetId)) {
      hits.push({ holderType, holderId, holderRef, field });
    }
  }

  function pushSingle(holder, holderType, holderId, holderRef, field) {
    if (holder[field] === targetId) {
      hits.push({ holderType, holderId, holderRef, field });
    }
  }

  switch (targetType) {
    case 'risk':
      for (const o of objectives) pushArray(o, 'objective',         o.id, o.ref || o.id, 'linkedRiskIds');
      for (const mr of mrs)       pushArray(mr,'management-review', mr.id, mr.ref || mr.id,'linkedRiskIds');
      for (const nc of nonconformities) pushArray(nc, 'nonconformity', nc.id, nc.ref || nc.id, 'linkedRiskIds');
      break;

    case 'asset':
      if (scopeRaw) pushArray(scopeRaw, 'scope', 'scope', 'scope', 'linkedAssetIds');
      for (const r of risks)            pushArray(r,  'risk',           r.id,  r.ref  || r.id,  'linkedAssetIds');
      for (const i of incidents)        pushArray(i,  'incident',       i.id,  i.ref  || i.id,  'linkedAssetIds');
      break;

    case 'authority':
      for (const i of incidents)      pushArray(i,  'incident',       i.id,  i.ref  || i.id,  'notifiedAuthorityIds');
      for (const lr of legalRegister) pushArray(lr, 'legal-register', lr.id, lr.ref || lr.id, 'linkedAuthorityIds');
      break;

    case 'specialInterestGroup':
      // v1: no cross-entity refs to special interest groups; case preserved for symmetric API.
      return [];

    case 'supplier':
      if (scopeRaw) {
        pushArray(scopeRaw, 'scope', 'scope', 'scope', 'linkedSupplierIds');
        if (Array.isArray(scopeRaw.outsourcedProcesses)) {
          for (const p of scopeRaw.outsourcedProcesses) {
            if (Array.isArray(p.linkedSupplierIds) && p.linkedSupplierIds.includes(targetId)) {
              hits.push({
                holderType: 'scope',
                holderId: 'scope',
                holderRef: 'scope',
                field: 'outsourcedProcesses[].linkedSupplierIds',
              });
              break;
            }
          }
        }
      }
      for (const r of risks)          pushArray(r,  'risk',           r.id,  r.ref  || r.id,  'linkedSupplierIds');
      for (const a of assets)         pushArray(a,  'asset',          a.id,  a.ref  || a.id,  'linkedSupplierIds');
      for (const i of incidents)      pushArray(i,  'incident',       i.id,  i.ref  || i.id,  'linkedSupplierIds');
      break;

    case 'incident':
      for (const r of risks)            pushArray(r,  'risk',              r.id,  r.ref || r.id, 'linkedIncidentIds');
      for (const nc of nonconformities) pushArray(nc, 'nonconformity',     nc.id, nc.ref || nc.id,'linkedIncidentIds');
      for (const mr of mrs)             pushArray(mr, 'management-review', mr.id, mr.ref || mr.id,'linkedIncidentIds');
      break;

    case 'audit':
      for (const nc of nonconformities) pushSingle(nc, 'nonconformity',    nc.id, nc.ref || nc.id, 'auditId');
      for (const mr of mrs)             pushArray (mr, 'management-review', mr.id, mr.ref || mr.id, 'linkedAuditIds');
      break;

    case 'nonconformity':
      for (const aud of audits) {
        if (Array.isArray(aud.findings)) {
          for (const f of aud.findings) {
            if (f.nonconformityId === targetId) {
              hits.push({
                holderType: 'audit',
                holderId: aud.id,
                holderRef: aud.ref || aud.id,
                field: 'findings[].nonconformityId',
              });
              break;
            }
          }
        }
      }
      for (const r of risks)     pushArray(r,  'risk',              r.id,  r.ref || r.id,  'linkedNonconformityIds');
      for (const i of incidents) pushArray(i,  'incident',          i.id,  i.ref || i.id,  'linkedNonconformityIds');
      for (const mr of mrs)      pushArray(mr, 'management-review', mr.id, mr.ref || mr.id,'linkedNonconformityIds');
      break;

    case 'objective':
      for (const m of metrics) pushSingle(m,  'metric',            m.id,  m.ref || m.id,  'objectiveId');
      for (const mr of mrs)    pushArray (mr, 'management-review', mr.id, mr.ref || mr.id,'linkedObjectiveIds');
      break;

    case 'metric':
      for (const o of objectives) pushArray(o,  'objective',         o.id,  o.ref || o.id,  'linkedMetricIds');
      for (const mr of mrs)       pushArray(mr, 'management-review', mr.id, mr.ref || mr.id,'linkedMetricIds');
      break;

    case 'document':
      for (const lr of legalRegister) pushArray(lr, 'legal-register', lr.id, lr.ref || lr.id, 'linkedDocumentIds');
      for (const a of assets)         pushArray(a,  'asset',          a.id,  a.ref || a.id,  'linkedDocumentIds');
      for (const i of incidents)      pushArray(i,  'incident',       i.id,  i.ref || i.id,  'linkedDocumentIds');
      break;

    case 'legal-register':
      for (const i of incidents) pushArray(i, 'incident', i.id, i.ref || i.id, 'linkedLegalRegisterIds');
      break;

    case 'customer':
      for (const lr of legalRegister) pushArray(lr, 'legal-register', lr.id, lr.ref || lr.id, 'triggeringCustomerIds');
      break;

    case 'management-review':
      // No holders point at MRs in §7 — the type is not a reference target.
      break;

    default:
      break;
  }

  return hits;
}
